import { useMemo, useState, type ReactNode } from 'react';
import { CalendarDays, MapPin, Users, Video } from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/api';
import { errMsg } from '@/lib/api-error';
import { useUiPreference } from '@/hooks/use-ui-preference';
import {
  useCalendarToday,
  useCalendarRange,
  type CalendarEvent,
} from '@/lib/home-today-store';
import {
  rangeForViewMode,
  daysInRange,
  isSameDay,
  startOfMonth,
  endOfMonth,
  type AgendaViewMode,
} from '@/lib/date-range';

import { formatarData, formatarDiaDaGrade } from '@/lib/format-date';
import { diaDaGrade } from '@/lib/datas-brasilia';
/**
 * AgendaHojeWidget — Espelho Google Agenda com toggle hoje/semana/mês
 * (R8 Bloco E + U.3 / FEAT-V2-014).
 *
 * Toggle persiste em `user_ui_preferences['agenda.view_mode']` via
 * `useUiPreference`. ZERO localStorage — preferência vive no SQLite.
 *
 * 3 layouts adaptativos:
 *   - today: lista linear vertical (R8 original)
 *   - week: grid 7 colunas (segunda→domingo)
 *   - month: calendar grid 7×N (com indicadores de eventos por dia)
 *
 * Privacy mode: decisão R8 #2 = mostrar tudo, mesmo private.
 */

/**
 * A-3: o 503 de `AGENDA_RANGE_TOGGLE` off responde `{ error: 'feature-disabled' }`
 * sem `message` — e o código chegou cru na Home. Flag desligada é estado, não
 * erro: ganha frase própria em vez do Alert vermelho.
 */
function agendaDesativada(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.response as { error?: string } | null | undefined)?.error === 'feature-disabled'
  );
}

export function AgendaHojeWidget() {
  const [viewMode, setViewMode] = useUiPreference<AgendaViewMode>(
    'agenda.view_mode',
    'today'
  );

  // viewMode='today' usa endpoint legado (cache backend separado).
  // viewMode='week'|'month' usa endpoint range novo (T.3 / FEAT-V2-014).
  const todayQuery = useCalendarToday();
  const rangeIso = useMemo(() => {
    if (viewMode === 'today') return null;
    const range = rangeForViewMode(viewMode);
    return { fromIso: range.start.toISOString(), toIso: range.end.toISOString() };
  }, [viewMode]);
  const rangeQuery = useCalendarRange(rangeIso?.fromIso ?? null, rangeIso?.toIso ?? null);

  const active = viewMode === 'today' ? todayQuery : rangeQuery;
  const { data, isLoading, isError, error, scopeInsufficient, notConfigured } = active;

  if (scopeInsufficient || notConfigured) {
    return null;
  }

  return (
    <Card>
      <div className="p-5">
        <header className="flex items-center justify-between mb-3 flex-wrap gap-2">
          {/* FASE-C: emoji 🗓️ saiu do título — Lucide only (regra do design system). */}
          <h2 className="font-semibold text-lg text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-ink-3 dark:text-ink-dark-3" aria-hidden />
            Sua agenda
          </h2>
          <div className="flex items-center gap-2">
            {data?.cached && (
              <Badge
                variant="neutral"
                size="sm"
                title="Mostrando a última sincronização salva — a agenda atualiza sozinha em instantes."
              >
                atualizando
              </Badge>
            )}
            <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
          </div>
        </header>

        {isLoading ? (
          <SkeletonList />
        ) : agendaDesativada(error) ? (
          // A-3 (auditoria 13/08): flag desligada não é falha de carregamento —
          // é um estado do sistema, e tem frase própria (sem código, sem flag).
          <p className="text-sm text-ink-3 dark:text-ink-dark-3 text-center py-8">
            A agenda está desativada.
          </p>
        ) : isError ? (
          <Alert variant="danger" rail>
            Não consegui carregar a agenda agora.
            <br />
            {/* A-3: errMsg em vez de error.message cru — no ApiError, `.message`
                é o CÓDIGO ("feature-disabled" chegou a aparecer na Home). */}
            <span className="text-xs opacity-70">{errMsg(error, 'Tente de novo em instantes.')}</span>
          </Alert>
        ) : data && data.events.length === 0 ? (
          <p
            className="text-sm text-ink-3 dark:text-ink-dark-3 text-center py-8"
            data-testid="agenda-empty"
          >
            {emptyMessage(viewMode)}
          </p>
        ) : data ? (
          viewMode === 'today' ? (
            <ul className="space-y-3" data-testid="agenda-list">
              {data.events.map((e) => (
                <EventRow key={e.id} event={e} />
              ))}
            </ul>
          ) : viewMode === 'week' ? (
            <WeekGrid events={data.events} />
          ) : (
            <MonthGrid events={data.events} />
          )
        ) : null}
      </div>
    </Card>
  );
}

function emptyMessage(viewMode: AgendaViewMode): string {
  if (viewMode === 'today') return 'Nenhum compromisso hoje';
  if (viewMode === 'week') return 'Nenhum compromisso esta semana';
  return 'Nenhum compromisso este mês';
}

// ─── Toggle [Hoje] [Semana] [Mês] ────────────────────────────────────────

function ViewModeToggle({
  viewMode,
  onChange,
}: {
  viewMode: AgendaViewMode;
  onChange: (m: AgendaViewMode) => void;
}) {
  const opts: Array<{ value: AgendaViewMode; label: string }> = [
    { value: 'today', label: 'Hoje' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mês' },
  ];
  return (
    <div
      className="inline-flex rounded-lg border border-line dark:border-line-dark overflow-hidden"
      role="tablist"
      aria-label="Visualização da agenda"
      data-testid="agenda-view-toggle"
    >
      {opts.map((o) => (
        <Button
          key={o.value}
          type="button"
          size="sm"
          variant={viewMode === o.value ? 'primary' : 'ghost'}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-none border-0',
            viewMode === o.value
              ? ''
              : 'hover:bg-paper-warm/60 dark:hover:bg-paper-dark-warm/60'
          )}
          role="tab"
          aria-selected={viewMode === o.value}
          data-testid={`agenda-toggle-${o.value}`}
        >
          {o.label}
        </Button>
      ))}
    </div>
  );
}

// ─── Layout: Week grid 7 colunas ─────────────────────────────────────────

function WeekGrid({ events }: { events: CalendarEvent[] }) {
  const range = useMemo(() => rangeForViewMode('week'), []);
  const days = useMemo(() => daysInRange(range), [range]);
  const eventsByDay = useMemo(() => groupEventsByDay(events, days), [events, days]);

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-7 gap-2"
      data-testid="agenda-week-grid"
    >
      {days.map((day) => (
        <div
          key={day.toISOString()}
          className={cn(
            'border border-line dark:border-line-dark rounded-lg p-2 min-h-[80px]',
            isSameDay(day, diaDaGrade()) && 'bg-red/5 border-red/30'
          )}
        >
          <div className="text-xs font-medium text-ink-2 dark:text-ink-dark-2 mb-2">
            <span className="uppercase">{formatarDiaDaGrade(day, { weekday: 'short' })}</span>
            <span className="ml-1 text-ink-3">{day.getDate()}</span>
          </div>
          <ul className="space-y-1">
            {(eventsByDay.get(day.toDateString()) ?? []).map((e) => (
              <li
                key={e.id}
                className="text-xs truncate cursor-pointer hover:underline"
                onClick={() => e.htmlLink && window.open(e.htmlLink, '_blank', 'noopener,noreferrer')}
                data-testid={`week-event-${e.id}`}
                title={e.summary}
              >
                <span className="text-ink-3 mr-1 tabular-nums">{formatTime(e.start.dateTime)}</span>
                <span className="text-ink-1 dark:text-ink-dark-1">{e.summary}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ─── Layout: Month calendar grid ─────────────────────────────────────────

function MonthGrid({ events }: { events: CalendarEvent[] }) {
  const range = useMemo(() => rangeForViewMode('month'), []);
  const days = useMemo(() => daysInRange(range), [range]);
  const monthStart = useMemo(() => startOfMonth(diaDaGrade()), []);
  const monthEnd = useMemo(() => endOfMonth(diaDaGrade()), []);
  const eventsByDay = useMemo(() => groupEventsByDay(events, days), [events, days]);

  // BUG-V2-018 / BF.2: state pra Dialog de eventos do dia.
  // Click numa célula seta selectedDay → abre modal listando eventos do dia.
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const selectedEvents = useMemo(
    () => (selectedDay ? eventsByDay.get(selectedDay.toDateString()) ?? [] : []),
    [selectedDay, eventsByDay],
  );

  return (
    <>
      <div
        className="grid grid-cols-7 gap-1 text-xs"
        data-testid="agenda-month-grid"
      >
        {/* Header dos dias da semana */}
        {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((d) => (
          <div
            key={d}
            className="text-center font-medium text-ink-3 uppercase tracking-wide py-1"
          >
            {d}
          </div>
        ))}
        {days.map((day) => {
          const inMonth = day >= monthStart && day <= monthEnd;
          const isToday = isSameDay(day, diaDaGrade());
          const dayEvents = eventsByDay.get(day.toDateString()) ?? [];
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => setSelectedDay(day)}
              className={cn(
                'border border-line/40 dark:border-line-dark/40 rounded-md p-1.5 min-h-[64px]',
                'text-left transition-colors',
                'hover:bg-paper-warm/40 dark:hover:bg-paper-dark-warm/40',
                'focus:outline-none focus:ring-2 focus:ring-red',
                !inMonth && 'opacity-40',
                isToday && 'border-red bg-red/5',
              )}
              data-testid={`month-day-${day.toISOString().slice(0, 10)}`}
              aria-label={`${day.getDate()} de ${formatarDiaDaGrade(day, { month: 'long' })}${dayEvents.length > 0 ? `, ${dayEvents.length} compromisso(s)` : ''}`}
            >
              <div
                className={cn(
                  'text-xs font-medium mb-1',
                  isToday && 'text-red',
                )}
              >
                {day.getDate()}
              </div>
              {dayEvents.length > 0 && (
                <div className="flex flex-wrap gap-0.5">
                  {dayEvents.slice(0, 3).map((e) => (
                    <span
                      key={e.id}
                      className="inline-block w-1.5 h-1.5 rounded-full bg-red"
                      title={`${formatTime(e.start.dateTime)} ${e.summary}`}
                      data-testid={`month-dot-${e.id}`}
                    />
                  ))}
                  {dayEvents.length > 3 && (
                    <span className="text-xs text-ink-3 ml-0.5">+{dayEvents.length - 3}</span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <DayEventsDialog
          day={selectedDay}
          events={selectedEvents}
          onClose={() => setSelectedDay(null)}
        />
      )}
    </>
  );
}

// ─── BUG-V2-018: Dialog com eventos do dia clicado ─────────────────

function DayEventsDialog({
  day,
  events,
  onClose,
}: {
  day: Date;
  events: CalendarEvent[];
  onClose: () => void;
}) {
  const dayLabel = formatarDiaDaGrade(day, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="sm">
      <DialogHeader>
        <DialogTitle>
          <span data-testid="day-events-title">{dayLabel}</span>
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        {events.length === 0 ? (
          <p
            data-testid="day-events-empty"
            className="text-sm text-ink-3 dark:text-ink-dark-3 italic text-center py-6"
          >
            Nenhum compromisso neste dia.
          </p>
        ) : (
          <ul data-testid="day-events-list" className="space-y-2">
            {events.map((e) => (
              <li
                key={e.id}
                data-testid={`day-event-${e.id}`}
                onClick={() =>
                  e.htmlLink && window.open(e.htmlLink, '_blank', 'noopener,noreferrer')
                }
                className={cn(
                  'flex gap-3 px-2 py-1.5 rounded-md',
                  e.htmlLink && 'cursor-pointer hover:bg-paper-warm dark:hover:bg-paper-dark-warm',
                )}
                role={e.htmlLink ? 'button' : undefined}
                tabIndex={e.htmlLink ? 0 : undefined}
              >
                <div className="w-14 flex-shrink-0 text-sm font-mono text-ink-2 dark:text-ink-dark-2 tabular-nums">
                  {formatTime(e.start.dateTime)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    'text-sm font-medium text-ink-1 dark:text-ink-dark-1 truncate',
                    e.isPrivate && 'italic',
                  )}>
                    {e.summary}
                  </p>
                  {e.location && (
                    <p className="text-xs text-ink-3 dark:text-ink-dark-3 truncate inline-flex items-center gap-1 max-w-full">
                      <MapPin className="w-3 h-3 shrink-0" aria-hidden />
                      <span className="truncate">{e.location}</span>
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function groupEventsByDay(events: CalendarEvent[], _days: Date[]): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    if (!e.start.dateTime) continue;
    // O dia do evento em Brasília (#484) — a grade também é de Brasília (diaDaGrade).
    const key = diaDaGrade(new Date(e.start.dateTime)).toDateString();
    const arr = map.get(key) ?? [];
    arr.push(e);
    map.set(key, arr);
  }
  return map;
}

function SkeletonList() {
  return (
    <ul className="space-y-3" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex gap-3 animate-pulse">
          <div className="w-12 h-4 bg-paper-warm dark:bg-paper-dark-warm rounded" />
          <div className="flex-1 h-4 bg-paper-warm dark:bg-paper-dark-warm rounded" />
        </li>
      ))}
    </ul>
  );
}

function EventRow({ event }: { event: CalendarEvent }) {
  const time = useMemo(() => formatTime(event.start.dateTime), [event.start.dateTime]);
  const handleClick = () => {
    if (event.htmlLink) {
      window.open(event.htmlLink, '_blank', 'noopener,noreferrer');
    }
  };
  return (
    <li
      data-testid={`agenda-event-${event.id}`}
      className="flex gap-3 group cursor-pointer hover:bg-paper-warm/40 dark:hover:bg-paper-dark-warm/40 -mx-2 px-2 py-1.5 rounded-lg transition-colors"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="w-14 flex-shrink-0 text-sm font-mono text-ink-2 dark:text-ink-dark-2 tabular-nums">
        {time}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <p
            className={cn(
              'text-sm font-medium text-ink-1 dark:text-ink-dark-1 truncate',
              event.isPrivate && 'italic'
            )}
          >
            {event.summary}
          </p>
          {event.isPrivate && (
            <Badge variant="neutral" size="sm" title="Evento marcado como privado">
              privado
            </Badge>
          )}
        </div>
        <EventMeta event={event} />
      </div>
    </li>
  );
}

// FASE-C: emojis 📍💻👥 viraram ícones Lucide (regra no-emoji do design system).
function EventMeta({ event }: { event: CalendarEvent }) {
  const bits: { key: string; icon: ReactNode; text: string }[] = [];
  if (event.location) {
    bits.push({ key: 'loc', icon: <MapPin className="w-3 h-3 shrink-0" aria-hidden />, text: event.location });
  }
  if (event.hangoutLink) {
    bits.push({ key: 'meet', icon: <Video className="w-3 h-3 shrink-0" aria-hidden />, text: 'Meet' });
  }
  if (event.attendees && event.attendees.length > 0) {
    bits.push({
      key: 'att',
      icon: <Users className="w-3 h-3 shrink-0" aria-hidden />,
      text: `${event.attendees.length} convidado${event.attendees.length === 1 ? '' : 's'}`,
    });
  }
  if (bits.length === 0) return null;
  return (
    <p className="text-xs text-ink-3 dark:text-ink-dark-3 truncate mt-0.5 flex items-center gap-2">
      {bits.map((b) => (
        <span key={b.key} className="inline-flex items-center gap-1 min-w-0">
          {b.icon}
          <span className="truncate">{b.text}</span>
        </span>
      ))}
    </p>
  );
}

function formatTime(iso: string): string {
  if (!iso) return '--:--';
  try {
    return formatarData(iso, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '--:--';
  }
}
